import { BookUser, Check, CheckSquare, Eye, FileText, Flag, Footprints, House, Image as ImageIcon, MapPin, Settings as SettingsIcon, Sparkles, UserPlus, Users, type LucideIcon } from "lucide-react";
import { loadHome } from "../homeResource";
/**
 * The console shell — sidebar, workspace switcher, and the frame the views
 * render into. Layout, spacing and chrome follow the source design
 * (`LedAI Webapp v2.html`, its SIDEBAR and MAIN blocks) rather than being
 * invented here.
 *
 * ## Console state lives in the URL
 *
 * `?workspace=`, `?groupBy=`, `?view=` and `?site=` are the whole of it.
 * `auth.ts` explains the reasoning for `workspace` — the active workspace is
 * part of WHAT PAGE YOU ARE ON, so it must survive a reload, a bookmark, the
 * back button and a pasted link — and the other three follow the same rule
 * for the same reason. Every link this shell generates goes through
 * `hrefWith`, which REWRITES one parameter and PRESERVES the rest; a link
 * that dropped `workspace` would silently return the user to their default
 * workspace on the next click, with the API perfectly correct underneath.
 *
 * ## Which view is on screen
 *
 * `?page=` names it — `brief`, `review`, or absent for the item list — and
 * `?item=<id>` opens Item detail over whatever page you were on, so Back
 * returns you there. The DAILY LOG is not a fourth page: it is what the
 * `?view=log` saved view renders, exactly as the source design does it
 * (`isLogView = vid === "log"`), because a structured daily log IS the log
 * view rather than a second destination that shows the same records twice.
 *
 * All of it lives in the URL for the reason `auth.ts` gives about
 * `workspace`: it is part of WHAT PAGE YOU ARE ON, so it must survive a
 * reload, a bookmark, the back button and a pasted link.
 *
 * ## The sidebar has two regions, and only one of them scrolls
 *
 * The page destinations, the saved views and the project list live in a
 * SCROLLING region; Policies, People and Invite live in a PINNED one above
 * the footer. The split is the fix for a real complaint: a workspace with 27
 * projects pushed all three admin destinations below the fold, so reaching
 * People meant scrolling past every job site the company runs. Their position
 * must not depend on how many projects a workspace happens to have — and the
 * project list itself is capped to `PROJECT_ROWS_COLLAPSED` rows with a
 * "Show all (N)" control, so it cannot own the sidebar's whole height either.
 *
 * ## The sidebar's order is the viewer's
 *
 * Three lists reorder — the top destinations, the VIEWS rows, and the
 * projects — by drag and by `Alt+ArrowUp` / `Alt+ArrowDown` on the focused
 * row. `useReorder.ts` explains why both: HTML5 drag emits nothing a
 * keyboard or a screen reader can drive, so a drag-only sidebar would be
 * unusable without a pointer.
 *
 * It is remembered in `localStorage`, which is right for a per-viewer display
 * preference and is allowed to be missing — a private window throws from the
 * accessor itself. `reorder.ts` answers "no stored order" for every such
 * case, so the sidebar renders naturally and forgets between reloads rather
 * than failing.
 *
 * The PINNED admin block does not reorder. Policies, People and Invite are
 * chrome: their position must not depend on a preference any more than it may
 * depend on how many projects a workspace has.
 *
 * ## What is deliberately inert
 *
 * Every destination in the sidebar is now live: Brief, Review, Ask
 * Led, the saved views, the project list, Policies, People, Invite, and ⌘K.
 * There is no PendingNav left, because there is nothing left pending.
 *
 * The FILTER CHIPS in `ItemList` and `DailyLog` remain inert, which is a
 * different decision: the design itself ships them inert (spec §6.3), so they
 * render exactly as drawn. Grouping is the functional control.
 *
 * ## Live updates (Plan 4, Task 5)
 *
 * The shell owns ONE `EventSource` on `/api/live` (`../live/liveConnection`)
 * for as long as it is mounted, and one `load()` that both the URL and that
 * stream call. A write landing in Mongo from `backend/` or the iOS app
 * therefore reaches an open console with no reload.
 *
 * Two things about that are deliberate and easy to undo by accident:
 *
 *  - **The stream is not re-opened when the user navigates.** Its effect
 *    depends only on the stable `load`, never on `search`; keying it on the
 *    URL would tear down and re-establish a long-lived connection on every
 *    click, which is both wasteful and a window in which events are missed.
 *  - **The connection's state is rendered.** A list that has quietly stopped
 *    updating while still looking current is worse than one that admits it is
 *    stale — the user's next decision is "reload" or "trust it", and only the
 *    console knows which is right.
 *
 * If the stream never connects at all — no `EventSource`, a 503 from a
 * console whose change stream is down, a proxy that eats it — every one of
 * those paths ends in a status, and the page is exactly the console it was
 * before this plan: correct, and refreshed by reloading.
 */

import { Fragment, useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { ResourceCacheContext, useResource } from "../useResource";
import { ResourceCache, inventoryQuery, resourceQuery, DATA_CHANGED_EVENT } from "../resourceCache";
import { createPortal } from "react-dom";
import {
  ApiError,
  fetchItems,
  fetchProjectDetails,
  type ConsoleGroupBy,
  type ItemsResponse,
  type Workspace,
} from "../api";
import type { ConsoleSort } from "../../server/projection/priority";
import { connectLive, type LiveStatus } from "../live/liveConnection";
import { C, FONT_MONO, H, projectDot } from "../tokens";
// Shared with the SERVER's bare-host redirect, which has to make the same
// call before any JavaScript runs — see `consoleUrlState.ts`.
import { companyEntryPath, ITEM_LIST_PARAMS } from "../../server/consoleUrlState";
import { consoleUrl, basePathFrom, BASE_PATH, MOUNT_PATH } from "../basePath";
import {
  ITEM_COLUMN_FILTERS,
  ItemList,
  projectSavedViews,
  projectSavedView,
  matchesSavedView,
  type SavedViewId,
  isSavedViewId,
} from "../views/ItemList";
import { ProjectsForType } from "../views/ProjectsForType";
import { SourceMark } from "../views/ProjectDetails";
import {
  defaultSourceFor,
  isRecordSource,
  rowsOfType,
  scopedSectionLabel,
  SOURCE_LABEL,
  SOURCE_ORDER,
  SOURCE_TYPES,
  sourceOf,
  typeLabel,
  type RecordSource,
  type ScopedType,
} from "./projectScope";
import { RECORD_FILTER_KEYS } from "../views/records/RecordsWorkspace";
import { ORDER_KEY } from "./reorder";
import { useMediaQuery } from "./useMediaQuery";
import { ProjectPicker } from "./ProjectPicker";
import { useReorder, type ReorderRowProps } from "./useReorder";
import { Brief } from "../views/Brief";
import { DailyLog } from "../views/DailyLog";
import { clearColumnFilterParams, columnFilterParam } from "../components/columnFilter";
import { columnSortParams } from "../components/columnSort";
import { ItemDetail } from "../views/ItemDetail";
import { ReviewQueue } from "../views/ReviewQueue";
import { ReviewSyncToast } from "../components/ReviewSyncToast";
import { ProjectHome } from "../views/ProjectHome";
import { WalkDetail } from "../views/WalkDetail";
import { AllWalks, WalksSkeleton } from "../views/AllWalks";
import { Photos } from "../views/Photos";
import { ProjectDetails, clearDetailsCache } from "../views/ProjectDetails";
import { IntegrationsDrawer } from "../components/IntegrationsDrawer";
import { OPEN_INTEGRATIONS_EVENT } from "../components/integrationsEvents";
import { looksLikeLedStaff } from "../views/TeamProcore";
import { ProjectBrief } from "../views/ProjectBrief";
import { AskLed } from "../views/AskLed";
import { Policies } from "../views/Policies";
import { People } from "../views/People";
import { Invite } from "../views/Invite";
import { CommandK } from "./CommandK";
import { Lockup } from "../components/Lockup";
import { AskComposer } from "../components/AskComposer";
import {
  AuthIdentity,
  resolveIdentity,
  useAuthPerson,
  type ResolvedIdentity,
} from "../auth/identity";
import { LoadingPanel, SIDEBAR_WIDTH, type ConsoleViewProps } from "../components/chrome";
import { RejectReasonBannerHost } from "../components/RejectReasonBanner";

// ---------------------------------------------------------------------------
// Pages
// ---------------------------------------------------------------------------

/** The console's top-level destinations. The Daily Log is deliberately NOT
 *  one — see the file header. */
export type ConsolePage =
  | "items"
  | "brief"
  | "review"
  | "walks"
  | "photos"
  | "project"
  | "project-brief"
  | "walk"
  | "ask"
  | "policies"
  | "people"
  | "invite"
  // Project details: one project's Directory and Locations. Offered in the
  // rail only with a project in scope (`?site=`).
  | "directory"
  | "locations";

const PAGES: ConsolePage[] = [
  "items",
  "brief",
  "review",
  "walks",
  "photos",
  "project",
  "project-brief",
  "walk",
  "ask",
  "policies",
  "people",
  "invite",
  "directory",
  "locations",
];

export function isConsolePage(value: string | null): value is ConsolePage {
  return !!value && (PAGES as string[]).includes(value);
}

// ---------------------------------------------------------------------------
// URL state
// ---------------------------------------------------------------------------

/**
 * `location.search` with ONE parameter rewritten and everything else kept.
 * Passing `null` removes the parameter. This is the only place the console
 * builds a link, so `workspace` cannot be dropped by omission anywhere.
 */
/** The query portion of `search`, whatever shape it arrived in. `hrefWith`
 *  can return a bare pathname ("/") when every parameter is cleared, and a
 *  chained call must not parse that pathname as a query — that is how the
 *  console once grew a parameter literally named "/" (`?%2F=`). */
function queryOf(search: string): string {
  const at = search.indexOf("?");
  if (at >= 0) return search.slice(at + 1);
  return search.startsWith("/") ? "" : search;
}

export function hrefWith(search: string, key: string, value: string | null): string {
  const params = new URLSearchParams(queryOf(search));
  if (key === "workspace" && params.get(key) !== value) params.delete("site");
  if (value === null) params.delete(key);
  else params.set(key, value);
  const query = params.toString();
  return query ? `?${query}` : location.pathname;
}

/**
 * Where "back" goes from an open walk.
 *
 * A walk is a detail of the Walks list no matter which link opened it, so back
 * returns to that list and the Walks tab takes the active state. This used to
 * read the currently-open project and return there when there was one, which
 * made the destination depend on how you arrived: the same walk, opened from
 * Home and from a project, sent you to two different places, and neither
 * matched the tab that was highlighted while you read it.
 *
 * `?project=` is deliberately kept — it is the workspace-wide filter the rest
 * of the console reads, not a breadcrumb, so dropping it here would silently
 * widen the list you land on.
 */
export function walksListHref(search: string): string {
  const params = new URLSearchParams(queryOf(search));
  // The open detail is the walk itself plus whatever record panel was over it.
  for (const key of ["walk", "item", "view"]) params.delete(key);
  params.set("page", "walks");
  return `?${params.toString()}`;
}

/** The `?workspace=` value naming a workspace, in `auth.ts`'s grammar. */
export function workspaceParam(workspace: Workspace): string {
  return workspace.type === "personal" ? "personal" : `team:${workspace.orgId}`;
}

/** A workspace boundary gets a fresh document, clearing data and live streams. */
export function workspaceHref(search: string, workspace: Workspace): string {
  const params = new URLSearchParams(queryOf(search));
  for (const key of ["workspace", "company", "site", "project", "item", "walk"]) params.delete(key);
  if (["project", "walk"].includes(params.get("page") ?? "")) params.set("page", "brief");
  if (pagePath() === HOME_PATH && !params.has("page") && ITEM_LIST_PARAMS.some((key) => params.has(key))) {
    params.set("page", "brief");
  }
  if (workspace.type === "personal") params.set("workspace", "personal");
  else if (!workspace.slug) params.set("workspace", workspaceParam(workspace));
  const root = workspace.type === "team" && workspace.slug
    ? `${MOUNT_PATH}/${encodeURIComponent(workspace.slug)}` : `${MOUNT_PATH}/`;
  return root + (params.size ? `?${params}` : "");
}

/**
 * What the switcher prints.
 *
 * The organization's NAME, which `auth.ts` carries beside the id — the
 * console used to print `org_3GWeACPHE6R2znD2Wr1oIixecyl` here and a user
 * read that as being signed into the wrong account.
 *
 * The id remains the fallback and is deliberately not dressed up: an
 * organization Clerk reported without a name has none, and an invented label
 * ("Team", or a blank pill) would leave two organizations indistinguishable.
 * An unreadable identifier at least identifies.
 */
export function workspaceLabel(workspace: Workspace): string {
  if (workspace.type === "personal") return "Personal";
  return workspace.name || workspace.orgId;
}

/**
 * What the identity strip's SECOND line says.
 *
 * It exists to label an id AS an id. `resolveIdentity` falls back to the
 * Clerk user id when neither Clerk nor the `User` mirror can name the
 * person — a real state, and better than a blank strip, which on a
 * signed-in console reads as "signed out". But printing `user_3E3fUozuA7b…`
 * in the bold slot where a name goes and saying nothing else is what made a
 * user believe they were signed into the wrong account. When that is all
 * there is, the console says which it is showing.
 */
export function identityCaption(
  identity: ResolvedIdentity,
  loaded: boolean,
  unauthenticated: boolean,
): string {
  if (!loaded) return unauthenticated ? "No session" : "…";
  return identity.source === "id" ? "Signed in · user ID" : "Signed in";
}

function sameWorkspace(a: Workspace, b: Workspace): boolean {
  if (a.type !== b.type) return false;
  return a.type === "personal" || a.orgId === (b as { orgId: string }).orgId;
}

// ---------------------------------------------------------------------------
// Small shared chrome
// ---------------------------------------------------------------------------

const META: React.CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 10.35,
  fontWeight: 700,
  letterSpacing: "1.2px",
  textTransform: "uppercase",
  color: C.sub2,
};

/**
 * How many projects the sidebar shows before "Show all (N)".
 *
 * Eight rows is roughly a third of the sidebar's height at 900px, which
 * leaves the four page destinations, the saved views and the pinned admin
 * block all on screen together at the console's own design width.
 */
const PROJECT_ROWS_COLLAPSED = 8;

/** One row in the sidebar's VIEWS section. The saved views and All Walks are
 *  the same shape deliberately — see `viewRows`. */
interface SidebarViewRow {
  id: string;
  name: string;
  hint: string;
  d1: string;
  d2?: string;
  active: boolean;
  /** Absent where the row genuinely has no count to print. Distinct from
   *  `"—"`, which means "counted, and the count failed". */
  count?: string | number;
  href: string;
}

/**
 * Sidebar text that COLLAPSES into the rail rather than disappearing from it.
 *
 * Every label in the sidebar used to be written `{!rail && <span>…}`, which
 * is why the collapse could only ever snap: the width changed in the same
 * frame the text left the document, so there was never anything on screen to
 * animate. The text stays mounted now and `main.tsx`'s `.navlabel` rules fade
 * it while the sidebar's own width squashes its box — see the comment there.
 *
 * `aria-hidden` on the rail is not decoration. The rail's label is the fly-out
 * (`data-tip`), and a screen reader that also read the collapsed text would
 * announce every row twice.
 */
function NavLabel({
  rail,
  style,
  children,
}: {
  rail?: boolean;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  return (
    <span className="navlabel" aria-hidden={rail || undefined} style={style}>
      {children}
    </span>
  );
}

function SectionLabel({ children, rail, mark, meta, testId }: {
  children: React.ReactNode;
  rail?: boolean;
  /** The source's mark before the words (the project-scoped nav). */
  mark?: React.ReactNode;
  /** Quiet trailing text — "4 proj". */
  meta?: string;
  testId?: string;
}) {
  // The rail has no labels to group, so the group's own label would be a word
  // floating over a column of icons (the design hides `.grp` there). It folds
  // to that spacer rather than being replaced by one, so the rows below it
  // slide up instead of jumping.
  return (
    <div
      className="navgrp"
      data-testid="nav-group-label"
      style={{
        ...META,
        // LED-1151: mono 8.5/700 at 1.2px, the design's quietest ink. The
        // gap between one group and the next lives in this label's own box.
        fontSize: 8.5,
        fontWeight: 700,
        letterSpacing: "1.2px",
        color: H.faint,
        lineHeight: 1.35,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        padding: rail ? "0" : "16px 12px 6px",
        // On the rail the label folds to the design's 1px divider.
        ...(rail ? { height: 1, background: H.edge, margin: "12px 6px" } : {}),
      }}
    >
      <NavLabel rail={rail} style={mark || meta ? { display: "flex", alignItems: "center", gap: 7 } : undefined}>
        {mark}
        <span data-testid={testId} style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{children}</span>
        {meta && <span style={{ flex: "none", color: C.railMeta, opacity: 0.75 }}>{meta}</span>}
      </NavLabel>
    </div>
  );
}

/** A sidebar destination that IS built. Live, and it carries the whole
 *  console URL forward — `?workspace=` above all. */
function PageNav({
  label,
  hint,
  active,
  href,
  onNavigate,
  icon: Icon,
  d1,
  d2,
  count,
  reorder,
  testId,
  rail,
  dense,
  badge,
}: {
  label: string;
  hint: string;
  active: boolean;
  href: string;
  onNavigate: (href: string) => void;
  /** The design's lucide glyph for this destination. `d1`/`d2` remain the
   *  drawing for anything without one (a saved view the design never drew). */
  icon?: LucideIcon;
  d1: string;
  d2?: string;
  /** Optional trailing count. Review carries one; the rest do not, and a nav
   *  row with nothing to count must print nothing rather than a zero. A saved
   *  view's count arrives already formatted, hence the string. */
  count?: number | string;
  /** Present on the reorderable destinations, absent on the pinned admin
   *  ones — those are chrome whose position must not depend on preference. */
  reorder?: ReorderRowProps;
  /**
   * Explicit test id. The default is derived from the LABEL, which made a
   * display rename ("Brief" -> "Home") silently retarget every test and
   * bookmarklet built on the old id — the top destinations pass a stable
   * one instead.
   */
  testId?: string;
  /** Icons only, with the label on hover — the design's icon rail. */
  rail?: boolean;
  /**
   * The 38px row rather than the 40px one — what the design gives Records
   * and the pinned admin rows. The top destinations are the taller row and
   * nothing else is: the two heights are how the rail groups itself once the
   * group labels are gone on the rail.
   */
  dense?: boolean;
  /**
   * Draw `count` as the design's gold badge instead of as a mono number.
   * Review's queue is the only count that is NEWS — the Records counts are
   * inventory, and a gold pill on each of six of them would make the rail
   * look like an alert list.
   */
  badge?: boolean;
}) {
  return (
    <a
      {...reorder}
      className={rail ? "kb navrow railrow" : "kb navrow"}
      href={href}
      // On the rail the fly-out label IS the label, so it carries the row's
      // name; the hint stays the `title` when there is room for both.
      data-tip={rail ? label : undefined}
      onMouseEnter={rail ? pinTip : undefined}
      onFocus={rail ? pinTip : undefined}
      title={rail ? label : hint}
      data-testid={testId ?? `nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      aria-current={active ? "page" : undefined}
      onClick={(event) => {
        event.preventDefault();
        onNavigate(href);
      }}
      style={{
        display: "flex",
        alignItems: "center",
        // The icon lands centred in the rail by PADDING rather than by
        // `justify-content`, which is the whole reason it can glide there:
        // padding interpolates and `center` does not. 6.5px is half of what
        // is left of the 32px row once its border and the 17px glyph have
        // had their share — 16 gutter + 1 border + 6.5 + 8.5 = 32, dead
        // centre of the 64px rail.
        gap: rail ? 0 : 12,
        // A FIXED height rather than a padded one: a row whose height is the
        // sum of its font's line box drifts with the face that happens to
        // load. LED-1151 (`home-projects.dc.html`) draws every nav row at
        // 38px, radius 9, `padding: 0 10px`, gap 12 — `dense` survives only
        // as the call sites' own grouping.
        height: dense ? 38 : 38,
        // On the 68px rail the 16px glyph centres by PADDING (which
        // interpolates) rather than `justify-content` (which snaps):
        // 10 gutter + 1 border + 15 + 8 = 34, dead centre of 68.
        padding: rail ? "0 15px" : "0 10px",
        borderRadius: 9,
        marginBottom: 0,
        // The border is on EVERY row, transparent, so selecting one never
        // moves its label by a pixel.
        border: "1px solid transparent",
        // The selected row is a plain WHITE surface on the warm nav ground —
        // the design's own treatment, no shadow, no brand fill. Only the
        // ACTIVE row pins its background inline; an inline "transparent"
        // would override the `.navrow:hover` stylesheet rule.
        ...(active ? { background: H.white } : {}),
      }}
    >
      {Icon ? (
        <Icon size={16} color={active ? H.ink : H.text} strokeWidth={1.8} aria-hidden style={{ flexShrink: 0 }} />
      ) : (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        // The icon carries the row's state as much as the label does: the
        // selected row's glyph is the label's own ink, a resting one's is
        // the rail's quiet 45%. One colour for both made a column of rows
        // where only the WORD changed.
        stroke={active ? H.ink : H.text}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        // An SVG is a flex item like any other and would otherwise be
        // squeezed along with the label it sits beside.
        style={{ flexShrink: 0 }}
      >
        <path d={d1} />
        {d2 && <path d={d2} />}
      </svg>
      )}
      <NavLabel
        rail={rail}
        style={{
          flex: 1,
          // The design's 14.5/600 for every row; the white surface is what
          // marks the selected one, not a weight jump.
          fontSize: 14.5,
          fontWeight: 600,
          color: active ? H.ink : H.text,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </NavLabel>
      {count !== undefined &&
        (badge ? (
          // The badge needs its own clipping wrapper, exactly as the ⌘K chip
          // does: padding and a background do not shrink, so a bare
          // `.navlabel` would leave a gold sliver on the rail long after the
          // number inside it had faded.
          <NavLabel rail={rail}>
            <span
              style={{
                display: "block",
                fontFamily: FONT_MONO,
                fontSize: 10.5,
                fontWeight: 700,
                color: H.amberInk,
                background: H.amberSoft,
                borderRadius: 999,
                padding: "2px 7px",
              }}
            >
              {count}
            </span>
          </NavLabel>
        ) : (
          <NavLabel rail={rail} style={{ fontFamily: FONT_MONO, fontSize: 10.5, fontWeight: 500, color: H.faint }}>
            {count}
          </NavLabel>
        ))}
    </a>
  );
}

// ---------------------------------------------------------------------------
// The workspace popover
// ---------------------------------------------------------------------------

/**
 * The workspace menu, as a FLOATING layer anchored to the pill.
 *
 * ## What was wrong
 *
 * The menu used to be a plain `<div>` rendered as the pill's next sibling, in
 * the sidebar's normal flow. Opening it therefore PUSHED the entire nav
 * column down by the menu's height — every destination, every saved view and
 * the whole project list — so the row under the pointer at the moment of the
 * click was no longer the row the user was aiming at. The user's words:
 * "clicking the workspace selection on the top left should not push
 * everything on the left bar down. It should open a floating bubble."
 *
 * ## Why a PORTAL and not `position: absolute`
 *
 * Taking the menu out of flow is enough to stop the reflow, but not enough to
 * stop it being CLIPPED. The sidebar is a `overflow: hidden` flex column
 * whose nav region is itself an `overflow: auto` scroll container, and an
 * absolutely positioned child of either is cut off at that box's edge — the
 * classic way a popover loses its bottom half or its overhang. A `fixed`
 * layer portalled to `document.body` has no such ancestor by construction,
 * which is why the popover is allowed to be WIDER than the rail it hangs off
 * and to overhang it: the overhang is what makes it read as a bubble
 * floating over the console rather than as another panel bolted to the rail.
 *
 * ## Dismissal, three ways
 *
 * Escape (focus returns to the pill), a click on anything else, and choosing
 * a workspace. The pill itself is deliberately NOT "anything else": it owns
 * the toggle, and closing on its `mousedown` too would close the menu and
 * immediately reopen it on the same press.
 *
 * ## Keyboard
 *
 * Opening moves focus INTO the menu, arrows move between entries and wrap,
 * Home/End jump to the ends, and every entry carries `.kb` so the console's
 * own focus ring (`main.tsx`) draws — the sidebar reorder work set that bar
 * when it added `Alt+Arrow`, for the same reason: a control only a pointer
 * can drive is not a control everybody has.
 */

/**
 * Distance from the pill's bottom edge to the popover's top.
 *
 * It was 6, which is less than the popover's own drop shadow reaches. The
 * bubble read as sitting ON the workspace row rather than under it — the row
 * you just clicked is the one thing that must stay legible while the menu is
 * open, because it is what tells you which workspace you are leaving. 12 is
 * daylight: enough that the shadow lands on the rail instead of on the row.
 */
const POPOVER_GAP = 12;

/**
 * How much WIDER than the pill the popover is.
 *
 * A bubble that matched the rail exactly would be indistinguishable from a
 * panel that had grown out of it, which is the very thing being fixed. The
 * overhang is only safe because of the portal above — inside the sidebar it
 * would be clipped.
 */
const POPOVER_OVERHANG = 28;

/** Never let the popover run off the right edge of the window. */
const POPOVER_VIEWPORT_MARGIN = 8;

interface PopoverBox {
  top: number;
  left: number;
  width: number;
}

/** Where the popover sits, in VIEWPORT coordinates — which is what `fixed`
 *  wants, and what makes the portal placement independent of every ancestor. */
function popoverBoxFor(anchor: HTMLElement | null): PopoverBox {
  if (!anchor) return { top: 0, left: 0, width: 0 };
  const rect = anchor.getBoundingClientRect();
  // The overhang is measured from the RAIL's edge, not the pill's: the pill
  // shares its row with the hide-toggle now, so its own width stops short of
  // the sidebar and a pill-relative popover would no longer float over the
  // console (adminViews' popover spec measures exactly that).
  const width = Math.max(rect.width, SIDEBAR_WIDTH - rect.left) + POPOVER_OVERHANG;
  const rightmost = Math.max(
    POPOVER_VIEWPORT_MARGIN,
    window.innerWidth - width - POPOVER_VIEWPORT_MARGIN,
  );
  return { top: rect.bottom + POPOVER_GAP, left: Math.min(rect.left, rightmost), width };
}

function WorkspacePopover({
  anchor,
  workspaces,
  activeWorkspace,
  search,
  onNavigate,
  onDismiss,
}: {
  /** The pill. Everything about the popover's position comes from its box. */
  anchor: HTMLElement | null;
  workspaces: Workspace[];
  activeWorkspace: Workspace | undefined;
  search: string;
  onNavigate: (href: string) => void;
  /** `true` returns focus to the pill — Escape does, an outside click does
   *  not, because that click is already moving focus somewhere deliberate. */
  onDismiss: (restoreFocus: boolean) => void;
}) {
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Measured at MOUNT, synchronously, rather than in an effect: an effect
  // would paint one frame at the top-left of the window before jumping into
  // place, which on a popover reads as a flicker.
  const [box, setBox] = useState<PopoverBox>(() => popoverBoxFor(anchor));

  useEffect(() => {
    const place = () => setBox(popoverBoxFor(anchor));
    place();
    addEventListener("resize", place);
    // Capture, so a scroll of ANY ancestor repositions it — a `fixed` layer
    // does not move with the thing it is anchored to on its own.
    addEventListener("scroll", place, true);
    return () => {
      removeEventListener("resize", place);
      removeEventListener("scroll", place, true);
    };
  }, [anchor]);

  // Focus goes into the menu on open. Without this the arrow keys have
  // nothing to act on and a keyboard user cannot reach the second
  // organization at all — which is the only route to it.
  useEffect(() => {
    menuRef.current?.querySelector<HTMLAnchorElement>('[role="menuitem"]')?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onDismiss(true);
    };
    const onPointerDown = (event: Event) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target)) return;
      // The pill owns the toggle — see the file header.
      if (anchor?.contains(target)) return;
      onDismiss(false);
    };
    // Bound to the DOCUMENT rather than to `window`: a menu that dismissed
    // only on events that made it all the way to the window would stay open
    // under anything that stopped propagation on the way.
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [anchor, onDismiss]);

  const focusItem = (resolve: (count: number, at: number) => number) => {
    const items = [
      ...(menuRef.current?.querySelectorAll<HTMLAnchorElement>('[role="menuitem"]') ?? []),
    ];
    if (items.length === 0) return;
    const at = items.indexOf(document.activeElement as HTMLAnchorElement);
    items[resolve(items.length, at)]?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Wrapping on purpose: a list of two or three that dead-ends at the
    // bottom makes the last entry feel unreachable from the first.
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusItem((count, at) => (at === -1 ? 0 : (at + 1) % count));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusItem((count, at) => (at === -1 ? count - 1 : (at - 1 + count) % count));
    } else if (event.key === "Home") {
      event.preventDefault();
      focusItem(() => 0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusItem((count) => count - 1);
    }
  };

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label="Switch workspace"
      data-testid="workspace-menu"
      className="pop"
      onKeyDown={onKeyDown}
      style={{
        position: "fixed",
        top: box.top,
        left: box.left,
        width: box.width,
        // Under ⌘K (60), which is a modal and must cover everything, and over
        // the Ask bubble and the Nudge sheet's scrim (40).
        zIndex: 50,
        border: `1px solid ${C.line}`,
        borderRadius: 12,
        overflow: "hidden",
        background: C.surface,
        boxShadow: `0 14px 34px ${C.line}, 0 2px 6px ${C.line}`,
      }}
    >
      {workspaces.map((workspace) => {
        const active = !!activeWorkspace && sameWorkspace(workspace, activeWorkspace);
        const href = workspaceHref(search, workspace);
        return (
          <a
            key={workspaceParam(workspace)}
            role="menuitem"
            className="kb"
            tabIndex={0}
            aria-current={active ? "true" : undefined}
            href={href}
            onClick={(event) => {
              event.preventDefault();
              onNavigate(href);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              padding: "9px 11px",
              background: active ? C.faint : "transparent",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: 4,
                background: workspace.type === "personal" ? C.ink : C.blue,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 14.95,
                fontWeight: active ? 700 : 600,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {workspaceLabel(workspace)}
            </span>
            {active && <span style={{ ...META, fontSize: 9.77 }}>Active</span>}
          </a>
        );
      })}
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Shell
// ---------------------------------------------------------------------------

/** The sidebar show/hide control (the design's `.navtoggle`): the panel
 *  icon, 32px in the sidebar's workspace row, 44px in the topbar. */
function NavToggle({
  tip,
  onClick,
  big,
  testId,
  rail,
}: {
  tip: string;
  onClick: () => void;
  big?: boolean;
  testId: string;
  /** Collapsed: the chevron in the glyph points the way it will open. */
  rail?: boolean;
}) {
  const size = big ? 44 : 30;
  return (
    <button
      className="kb askc-btn navtoggle"
      title={tip}
      aria-label={tip}
      data-testid={testId}
      onClick={onClick}
      style={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: 8,
        background: "transparent",
        color: H.sub,
        display: "grid",
        placeItems: "center",
        padding: 0,
        flexShrink: 0,
      }}
    >
      <svg
        width={big ? 18 : 16}
        height={big ? 18 : 16}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <rect x="3" y="4" width="18" height="16" rx="3" />
        <path d="M9 4v16" />
        <path d={rail ? "M14 10l2 2-2 2" : "M16 10l-2 2 2 2"} />
      </svg>
    </button>
  );
}

/** The design's glyph for each destination ("Daily Report Editor" sidebar). */
const NAV_ICONS: Record<string, LucideIcon> = {
  brief: House,
  review: Check,
  team: Users,
  ask: Sparkles,
  walks: Footprints,
  log: FileText,
  obs: Eye,
  issues: Eye,
  task: CheckSquare,
  punch: Flag,
  photos: ImageIcon,
  directory: BookUser,
  locations: MapPin,
};

/** The icon rail's width — LED-1151's collapsed nav (`navW: '68px'`). */
const RAIL_WIDTH = 68;

/** How long the phone's drawer stays mounted after it is closed, so it can
 *  animate out. Must match `[data-nav="drawer"][data-closing]` in `main.tsx`. */
const DRAWER_CLOSE_MS = 190;

/**
 * Pin a rail row's fly-out label beside the row.
 *
 * The label is a `position: fixed` pseudo-element (see `main.tsx`), because it
 * has to escape a sidebar that clips its overflow — and a fixed element has no
 * idea which row it belongs to, so the row hands it its own vertical centre.
 * Measured on hover rather than on render: the list scrolls.
 */
function pinTip(event: { currentTarget: HTMLElement }): void {
  const box = event.currentTarget.getBoundingClientRect();
  event.currentTarget.style.setProperty("--tip-y", `${box.top + box.height / 2}px`);
}

/** The Home page's clean path (mount-relative). `basePath.ts` knows to strip
 *  it when deriving the mount, so a reload on it cannot corrupt API URLs. */
const HOME_PATH = "/home";

/** The pathname with the mount prefix removed — the part the SHELL routes on. */
function pagePath(): string {
  const base = consoleUrl("/").replace(/\/$/, "");
  const path = location.pathname.startsWith(base)
    ? location.pathname.slice(base.length)
    : location.pathname;
  return path.replace(/\/+$/, "") || "/";
}



export function Shell() {
  const [cache] = useState(() => new ResourceCache());
  return <ResourceCacheContext.Provider value={cache}><CachedShell cache={cache} /></ResourceCacheContext.Provider>;
}

function CachedShell({ cache }: { cache: ResourceCache }) {
  useSyncExternalStore(cache.subscribe, cache.version, cache.version);
  const [search, setSearch] = useState(() => location.search);
  const [path, setPath] = useState(() => pagePath());
  const inventoryKey = `inventory${inventoryQuery(search)}`;
  const [loaded, setLoaded] = useState<{ key: string; purge: number; data: ItemsResponse } | null>(null);
  const data = loaded?.key === inventoryKey && loaded.purge === cache.purgeVersion() ? loaded.data : cache.get<ItemsResponse>(inventoryKey);
  /**
   * A workspace change is a scope boundary, not ordinary navigation. Keep
   * the destination's identity and switcher choices while its first request
   * is in flight, but never leave the previous workspace's counts, projects,
   * or rows under the newly selected name.
   */
  const [pendingWorkspace, setPendingWorkspace] = useState<Workspace | null>(null);
  const [knownWorkspaces, setKnownWorkspaces] = useState<Workspace[]>([]);
  const [failureValue, setFailure] = useState<ApiError | null>(null);
  const [refreshFailureValue, setRefreshFailure] = useState<ApiError | null>(null);
  const [failedKey, setFailedKey] = useState<string | null>(null);
  const failure = failedKey === inventoryKey ? failureValue : null;
  const refreshFailure = failedKey === inventoryKey ? refreshFailureValue : null;
  const [loading, setLoading] = useState(true);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [live, setLive] = useState<LiveStatus>("connecting");
  /** Bumped when the live stream says something moved. The page views load
   *  off it, and `useResource` reads a bump as a REFRESH rather than a
   *  navigation — which is what keeps a failed background poll from blanking
   *  a page that is still correct. */
  const [refreshKey, setRefreshKey] = useState(0);
  /** ⌘K / Ctrl-K. Not URL state, deliberately: the palette is a transient
   *  way to GET somewhere, and a bookmark that reopened it would be a
   *  bookmark to a search box rather than to a page. */
  const [paletteOpen, setPaletteOpen] = useState(false);
  /** The sidebar project picker. Held here so ⌘P can open it. */
  const [pickerOpen, setPickerOpen] = useState(false);
  /**
   * Whether the project list is showing everything.
   *
   * NOT in the URL, unlike every other piece of console state: this is how
   * much of one list you can see, not what page you are on. A bookmark that
   * restored it would be a bookmark to a scroll position.
   */
  const [showAllProjects, setShowAllProjects] = useState(false);
  /**
   * The sidebar, hideable (the settled design's `.navtoggle`). Desktop: a
   * persisted preference — the reader who hides it means it. Mobile (the
   * design's 820px breakpoint): the sidebar is an off-canvas DRAWER, closed
   * by default and never persisted — a drawer that reopened itself on every
   * navigation would be a wall, not a door.
   */
  const mobile = useMediaQuery("(max-width: 820px)");
  /**
   * Desktop has TWO states, not three: `open` and the design's icon RAIL.
   * There is deliberately no "gone" on a desktop — the toggle used to hide the
   * sidebar outright, which reclaimed 204px and took the workspace, the
   * destinations and the project list with it, leaving a reader who wanted a
   * wider page with no way to navigate. The rail keeps every row reachable at
   * 64px, and each one names itself on hover (`.railrow`).
   *
   * Persisted under the design's own key. A reader who had hidden the sidebar
   * under the old key lands on the rail, which is the state that preference
   * was reaching for.
   */
  const [navPref, setNavPref] = useState<"open" | "rail">(() => {
    try {
      const stored = localStorage.getItem("ledai.nav2");
      if (stored === "rail" || stored === "open") return stored;
      return localStorage.getItem("ledai.nav") === "collapsed" ? "rail" : "open";
    } catch {
      return "open";
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  /**
   * The drawer outlives its own `false` by the length of the close animation.
   *
   * On a desktop the sidebar never unmounts, so the collapse is a pure CSS
   * transition and needs nothing from React. The phone's drawer DOES unmount,
   * and an element removed from the document cannot animate out of it — the
   * door would vanish rather than swing shut. `drawerPresent` keeps it mounted
   * for `DRAWER_CLOSE_MS` with `data-closing`, which is the hook `main.tsx`
   * reverses the open animation on; the timer is cleared on reopen so a fast
   * open/close/open cannot unmount a drawer that is on screen again.
   */
  const [drawerPresent, setDrawerPresent] = useState(false);
  useEffect(() => {
    if (data?.availableWorkspaces.length) {
      setKnownWorkspaces(data.availableWorkspaces);
    }
  }, [data]);

  useEffect(() => {
    if (data && pendingWorkspace && sameWorkspace(data.activeWorkspace, pendingWorkspace)) {
      setPendingWorkspace(null);
    }
  }, [data, pendingWorkspace]);

  useEffect(() => {
    if (drawerOpen) {
      setDrawerPresent(true);
      return;
    }
    if (!drawerPresent) return;
    const timer = setTimeout(() => setDrawerPresent(false), DRAWER_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [drawerOpen, drawerPresent]);
  /** The full-width sidebar. The rail is a sidebar too — see `rail`. */
  const sidebarVisible = !mobile && navPref === "open";
  /** Icons only, labels on hover. Never on a phone: there the sidebar is an
   *  off-canvas drawer, and a 64px rail beside a 390px page is a wall. */
  const rail = !mobile && navPref === "rail";
  const setNav = useCallback((next: "open" | "rail") => {
    setNavPref(next);
    try {
      localStorage.setItem("ledai.nav2", next);
    } catch {
      // Storage may be unavailable (private window) — the toggle still works
      // for this visit and simply forgets.
    }
  }, []);

  // Back/forward must change the console, not just the address bar.
  useEffect(() => {
    const onPop = () => {
      setSearch(location.search);
      setPath(pagePath());
    };
    addEventListener("popstate", onPop);
    return () => removeEventListener("popstate", onPop);
  }, []);

  // ⌘K on a Mac, Ctrl-K elsewhere. `preventDefault` matters: Ctrl-K is
  // "search bookmarks" in some browsers, and a palette that opened BEHIND a
  // browser popup would look broken.
  //
  // ⌘B is the design's own sidebar shortcut, and it is the reason the rail is
  // worth having: a reader who wants the page wide for a minute presses it
  // twice rather than hunting for a control.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!event.metaKey && !event.ctrlKey) return;
      const key = event.key.toLowerCase();
      if (key === "k") {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
      // ⌘P, the picker's own shortcut (the design prints it in the search
      // field). It takes the browser's print shortcut on purpose; the picker
      // lives in the open sidebar, so the sidebar opens first if it is not.
      if (key === "p" && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        const hidden = mobile ? !drawerOpen : navPref !== "open";
        if (mobile) setDrawerOpen(true);
        else if (navPref !== "open") setNav("open");
        // Toggle only when the picker is on screen to toggle; otherwise open.
        setPickerOpen((open) => (hidden ? true : !open));
      }
      if (key === "b") {
        event.preventDefault();
        if (mobile) setDrawerOpen((open) => !open);
        else setNav(navPref === "open" ? "rail" : "open");
      }
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [mobile, navPref, setNav, drawerOpen]);

  const navigate = useCallback((href: string) => {
    const target = new URL(href, location.href);
    const targetBase = basePathFrom(target.pathname);
    if (targetBase !== BASE_PATH) {
      location.assign(target.href);
      return;
    }
    const requestedWorkspace = new URLSearchParams(queryOf(href)).get("workspace");
    const destination = requestedWorkspace
      ? data?.availableWorkspaces.find(
          (workspace) => workspaceParam(workspace) === requestedWorkspace,
        ) ?? (requestedWorkspace === "personal" ? { type: "personal" as const } : null)
      : null;

    if (
      destination &&
      (!data?.activeWorkspace || !sameWorkspace(destination, data.activeWorkspace))
    ) {
      // Do this before the URL update. React batches these writes, so the
      // next paint already has the selected workspace and no stale scope.
      setPendingWorkspace(destination);
      setLoaded(null);
      setFailure(null);
      setRefreshFailure(null);
    }
    if (destination && BASE_PATH !== MOUNT_PATH) {
      location.assign(workspaceHref(href, destination));
      return;
    }
    history.pushState(null, "", href);
    setSearch(location.search);
    setPath(pagePath());
    setSwitcherOpen(false);
    // A navigation from the mobile drawer is the drawer doing its job —
    // it closes so the page it opened is what the reader sees.
    setDrawerOpen(false);
  }, [data]);

  /**
   * The workspace pill — the popover's anchor, and where focus goes back to.
   *
   * Dismissing with Escape must not strand a keyboard user in the document
   * body with no way back to the control they just came from; an outside
   * click is already moving focus somewhere deliberate, so it does not.
   */
  const pillRef = useRef<HTMLButtonElement | null>(null);
  const closeSwitcher = useCallback((restoreFocus: boolean) => {
    setSwitcherOpen(false);
    if (restoreFocus) pillRef.current?.focus();
  }, []);

  /**
   * The console's one fetch, reached from two directions: the URL changing,
   * and the live stream saying something changed.
   *
   * It is a stable callback reading the CURRENT url out of a ref rather than
   * a closure over `search`, because the live effect must not be re-run — and
   * the stream therefore not re-opened — every time the user clicks a view
   * (see the file header). The ref is also what makes a refresh that was
   * scheduled before a navigation fetch the page the user is on NOW.
   *
   * `token` is the staleness guard the previous `cancelled` flag was: two
   * loads can be in flight at once (a navigation landing on top of a live
   * refresh), and only the newest may write state. Without it the slower of
   * the two wins, which is how a console ends up showing the previous
   * workspace's rows.
   */
  const searchRef = useRef(search);
  searchRef.current = search;
  /** Read inside `load`'s catch, which must not close over a stale `data`. */
  const loadedRef = useRef(loaded);
  loadedRef.current = loaded;
  const loadToken = useRef(0);

  /** Cached navigation and background refresh both retain the destination's
   * data on transient failure. A denial always removes it. */
  const load = useCallback(async () => {
    const token = (loadToken.current += 1);
    setLoading(true);
    const currentSearch = searchRef.current;
    const key = `inventory${inventoryQuery(currentSearch)}`;
    try {
      const query = new URLSearchParams(currentSearch);
      const isHome = !query.get("item") && !query.get("walk") &&
        (query.get("page") === "brief" || (!query.get("page") &&
          (pagePath() === HOME_PATH || !ITEM_LIST_PARAMS.some(name => query.has(name)))));
      // Legacy list filters retain their existing inventory contract. Normal
      // Home shares the same pending request as Brief's resource hook.
      const homeInventory = isHome && !["groupBy", "stalledOnly", "sort", "severity"].some(name => query.has(name));
      const next = await cache.load(key, () => homeInventory
        ? loadHome(cache, currentSearch).then(home => home.inventory)
        : fetchItems(currentSearch));
      if (token !== loadToken.current) return;
      if (next.activeWorkspace.type === "team" && next.activeWorkspace.slug) {
        const canonicalBase = `${MOUNT_PATH}/${encodeURIComponent(next.activeWorkspace.slug)}`;
        if (BASE_PATH !== canonicalBase) {
          location.replace(companyEntryPath(new URL(location.href), next.activeWorkspace.slug, MOUNT_PATH));
          return;
        }
      }
      setLoaded({ key, purge: cache.purgeVersion(), data: next });
      setFailure(null);
      setRefreshFailure(null);
    } catch (error: unknown) {
      if (token !== loadToken.current) return;
      const apiError =
        error instanceof ApiError
          ? error
          : new ApiError(0, (error as Error).message || "Request failed");
      setFailedKey(key);
      const denied = [401, 403, 404].includes(apiError.status);
      const previous = loadedRef.current;
      const retained = previous?.key === key && previous.purge === cache.purgeVersion()
        ? previous.data : cache.get<ItemsResponse>(key);
      if (retained && !denied) {
        setRefreshFailure(apiError);
      } else {
        if (denied) cache.clear();
        setLoaded(null);
        setFailure(apiError);
        setRefreshFailure(null);
      }
    } finally {
      if (token === loadToken.current) setLoading(false);
    }
  }, [cache]);

  useEffect(() => {
    void load();
  }, [inventoryKey, load]);

  const refresh = useCallback(async (purge = false) => {
    cache.invalidate(purge);
    setRefreshKey((key) => key + 1);
    await load();
  }, [cache, load]);

  useEffect(() => {
    const changed = (event: Event) => {
      const detail = (event as CustomEvent<{ purge?: boolean }>).detail;
      void refresh(detail?.purge === true);
    };
    window.addEventListener(DATA_CHANGED_EVENT, changed);
    return () => window.removeEventListener(DATA_CHANGED_EVENT, changed);
  }, [refresh]);

  useEffect(() => {
    // Walks, photos, and time-based counts can change without an SSE record
    // event. Keep reconciling every minute even while the stream is live.
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  /**
   * One live connection for the life of the shell. `connectLive` never
   * throws: an environment with no `EventSource`, a 503, or a stream the
   * server closes all resolve to a `LiveStatus`, and the console below
   * renders every row it already has either way.
   */
  // REST queries carry the selected workspace; the event stream must carry
  // the same scope or a Personal page subscribes to the default team's sites
  // and looks "Live" while every relevant record event is filtered out.
  const liveScope = new URLSearchParams();
  const scopeParams = new URLSearchParams(search);
  for (const key of ["workspace", "company"]) {
    const value = scopeParams.get(key);
    if (value !== null) liveScope.set(key, value);
  }
  const liveUrl = consoleUrl(`/api/live${liveScope.size ? `?${liveScope}` : ""}`);
  useEffect(() => {
    const connection = connectLive({
      url: liveUrl,
      // The page views reload off `refreshKey`; the item list is AWAITED, so
      // the coalescer's window still cannot open faster than the server can
      // answer (`liveConnection`'s "bounded by how fast the server can
      // answer, never by how fast records arrive").
      onRefresh: refresh,
      onDiscard: () => {
        // Scope/delete/closed events remove all mounted snapshots immediately,
        // including a response already in flight before the event.
        loadToken.current += 1;
        cache.invalidate(true);
        setLoaded(null);
        void load();
      },
      onStatus: setLive,
    });
    setLive(connection.status());
    return () => connection.close();
  }, [refresh, liveUrl]);

  const params = new URLSearchParams(search);
  const rawView = params.get("view");
  const activeSiteId = params.get("site");
  // "All projects" in a workspace whose every project is on Autodesk reads as
  // Autodesk (Daily reports, Issues). A mixed or Procore workspace keeps the
  // Procore vocabulary there, as before.
  const allSitesAutodesk = !!data?.sites.length && data.sites.every((site) => site.syncSource === "autodesk");
  const activeIntegration = activeSiteId
    ? data?.sites.find((site) => site.id === activeSiteId)?.syncSource
    : allSitesAutodesk ? "autodesk" : undefined;
  const view: SavedViewId = projectSavedView(isSavedViewId(rawView) ? rawView : "all", activeIntegration);

  /**
   * The scoped project's details SUMMARY — the rail's Directory/Locations
   * counts and the pages' header. It never costs a provider call: the
   * backend answers it from snapshots already read (see
   * `projectDetails.service.ts`). Only with a project in scope.
   *
   * Not tied to the shell's live `refreshKey`: a record arriving on a walk
   * changes nothing in a directory. `detailsReload` is bumped by the page
   * after an upload, or when its own read is the first to fill a count.
   */
  const [detailsReload, setDetailsReload] = useState(0);
  /** The account menu's Integrations drawer — opened by an event, because the
   *  menu is Clerk's (`AuthGate`) and the "Sign in to Procore" prompts are
   *  inside views; neither may import the shell. */
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  useEffect(() => {
    const open = () => setIntegrationsOpen(true);
    window.addEventListener(OPEN_INTEGRATIONS_EVENT, open);
    return () => window.removeEventListener(OPEN_INTEGRATIONS_EVENT, open);
  }, []);
  // After the workspace read settles: a provider directory is the slowest
  // read on the page and must not compete with the one every view waits on.
  const detailsKey = activeSiteId && (data || failure)
    ? `project-details:${activeSiteId}${resourceQuery(search, ["workspace"])}`
    : null;
  const details = useResource(
    detailsKey,
    () => fetchProjectDetails(activeSiteId!, search),
    detailsReload,
  );

  /**
   * A project id belongs to a workspace, while the URL belongs to the
   * browser. Workspace links clear `site` immediately; for stale bookmarks,
   * once the response names its readable projects, discard a site it cannot
   * contain and reload the scope without it. Keeping
   * it would leave Personal on a permanently empty, invisible project filter.
   *
   * We intentionally validate against `data.sites`, not the rows: an empty
   * personal project is still a valid selection and must not be cleared.
   */
  useEffect(() => {
    if (!data || !activeSiteId || data.sites.some((site) => site.id === activeSiteId)) {
      return;
    }
    const cleared = hrefWith(search, "site", null);
    if (cleared === search) return;
    history.replaceState(null, "", cleared);
    setSearch(location.search);
  }, [activeSiteId, data, search]);
  const rawPage = params.get("page");
  /**
   * Which page is on screen. `?page=` wins when present; the Home page's
   * clean path answers next; a bare URL LANDS ON HOME — loading the console
   * is asking "where do things stand", not "list everything". A page-less
   * URL that carries item-list state (`?view=`, `?sort=`, …) is a bookmark
   * from before the Home landing and still opens the list it named.
   */
  const legacyListUrl = ITEM_LIST_PARAMS.some((key) => params.get(key) !== null);
  const page: ConsolePage = isConsolePage(rawPage)
    ? rawPage
    : rawPage !== null
      ? // A STALE `?page=` (a bookmark of a page that no longer exists) falls
        // back to the item list, as it always has — only a page-LESS URL is
        // the "where do things stand" landing.
        "items"
      : path === HOME_PATH || (!legacyListUrl && !params.get("item") && !params.get("walk"))
        ? "brief"
        : "items";

  // Canonicalize the landing once: a bare URL shows Home, so it should READ
  // as Home. replaceState, not push — Back must leave the site, not bounce.
  useEffect(() => {
    if (rawPage === null && page === "brief" && pagePath() !== HOME_PATH && BASE_PATH === MOUNT_PATH) {
      history.replaceState(null, "", consoleUrl(HOME_PATH) + location.search);
      setPath(pagePath());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- landing only
  }, []);
  /** `?item=` opens Item detail OVER whatever page you were on, so Back
   *  returns you there rather than to a fixed home. */
  const openItemId = params.get("item");
  /**
   * `?project=` and `?walk=` name WHICH project or walk `?page=project` /
   * `?page=walk` is about. `?project=` doubles as the Review queue's project
   * filter, deliberately: one parameter means "which project", and the page
   * on screen decides what to do with it, so a project home's "Review 4"
   * button is just `?page=review` on the URL you were already on.
   */
  const openProjectId = params.get("project");
  const openWalkId = params.get("walk");
  /**
   * What is OPEN, as one string — the identity the pane's entrance replays on.
   *
   * Not the search string: filtering, sorting and paging are not openings, and
   * keying on the whole URL would replay the animation on every chip press.
   * The saved `view` IS included, because switching Punch to Tasks really is
   * arriving somewhere new.
   */
  const viewKey = openItemId
    ? `item:${openItemId}`
    : openWalkId
      ? `walk:${openWalkId}`
      : openProjectId
        ? `project:${page}:${openProjectId}`
        : `page:${page}:${view ?? ""}`;
  /** Flips whenever `viewKey` does, so the pane's entrance animation gets a
   *  new NAME and replays — without remounting anything. See the wrapper. */
  const [enterParity, setEnterParity] = useState(0);
  const lastViewKey = useRef(viewKey);
  useEffect(() => {
    if (lastViewKey.current === viewKey) return;
    lastViewKey.current = viewKey;
    setEnterParity((parity) => (parity === 0 ? 1 : 0));
  }, [viewKey]);

  /** Every link and every navigation the views make. Both go through
   *  `hrefWith`, so `?workspace=` and the rest of the console state ride
   *  along — a view that built its own link would silently drop them. */
  const hrefForItem = (itemId: string) => hrefWith(search, "item", itemId);
  const backHref = hrefWith(search, "item", null);

  /** A destination link: it sets `?page=`, clears `?item=` (a detail overlay
   *  must not survive a change of page), and preserves everything else —
   *  `?workspace=` above all. */
  const hrefForPage = (page: ConsolePage, extra?: [string, string | null]) => {
    let next = hrefWith(
      hrefWith(search, "item", null),
      "page",
      // Home routes by PATH — a page parameter would fight the pathname.
      page === "brief" ? null : page,
    );
    // Home closes the chat and leaves the saved record view. Keep that state
    // on Ask/citation links for Back, but not on a fresh Home visit.
    if (page === "brief") {
      for (const key of ["view", "ask", "askChat", "askSubmit"]) next = hrefWith(next, key, null);
    }
    if (extra) next = hrefWith(next, extra[0], extra[1]);
    const query = next.startsWith("?") ? next : "";
    if (BASE_PATH !== MOUNT_PATH) {
      // Company root is Home; explicit page state distinguishes filtered lists.
      const params = new URLSearchParams(query);
      params.set("page", page);
      return BASE_PATH + `?${params}`;
    }
    return (page === "brief" ? consoleUrl(HOME_PATH) : consoleUrl("/")) + query;
  };

  /** The Review queue clears `?walk=`. Without that, arriving from a walk
   *  would silently narrow the whole queue to that one walk while the page
   *  said "All projects" — a filter nobody chose and nothing on screen
   *  explains. */
  const hrefForReview = (jobSiteId?: string) =>
    hrefWith(hrefForPage("review", ["project", jobSiteId ?? null]), "walk", null);
  const hrefForProject = (jobSiteId: string) => hrefForPage("project", ["project", jobSiteId]);
  const hrefForWalk = (visitId: string) => hrefForPage("walk", ["walk", visitId]);
  const walkBackHref = walksListHref(search);

  const viewProps: ConsoleViewProps = {
    search,
    refreshKey,
    pendingReviewCount: data?.pendingReviewCount ?? null,
    inventory: data ?? null,
    personalHref: workspaceHref(search, { type: "personal" }),
    onGoPersonal: () => navigate(workspaceHref(search, { type: "personal" })),
    hrefForItem,
    onOpenItem: (itemId: string) => navigate(hrefForItem(itemId)),
    hrefWithParam: (key: string, value: string | null) => hrefWith(search, key, value),
    onNavigate: navigate,
    hrefForProject,
    onOpenProject: (jobSiteId: string) => navigate(hrefForProject(jobSiteId)),
    hrefForWalk,
    onOpenWalk: (visitId: string) => navigate(hrefForWalk(visitId)),
    hrefForReview,
    onOpenReview: (jobSiteId?: string) => navigate(hrefForReview(jobSiteId)),
  };

  const rows = (data?.groups ?? []).flatMap((group) => group.rows);
  const sites = data?.sites ?? [];

  /**
   * The project list is CAPPED, and the rest is behind "Show all (N)".
   *
   * A real workspace here has 27 projects. Rendered in full they ran the
   * sidebar's whole height, which is a list nobody reads and — before the
   * admin block moved out of the scrolling region — pushed Policies, People
   * and Invite below the fold.
   *
   * Capped by ROW COUNT rather than by a pixel `max-height`, because the rows
   * are a uniform height: counting gives the same fixed maximum height
   * without ever clipping a row through its middle, which is what a pixel cap
   * with `overflow: hidden` does to whichever project lands on the boundary.
   *
   * It is REORDERED first and capped second. The other way round would make
   * "Show all" the only way to reach a project you had just moved to the top,
   * and would let the cap decide which projects are eligible to be moved at
   * all.
   */
  const projects = useReorder(ORDER_KEY.projects, sites, (site) => site.id);
  const visibleSites = showAllProjects
    ? projects.ordered
    : projects.ordered.slice(0, PROJECT_ROWS_COLLAPSED);
  const hiddenProjectCount = sites.length - PROJECT_ROWS_COLLAPSED;

  /** Open work per site, for the sidebar's count column. "Open" is anything
   *  the projection did not classify as `closed` or `logged` — a daily log is
   *  a record, not work, which is exactly why `logged` is its own status. */
  const openCountFor = (siteId: string) =>
    rows.filter(
      (row) => row.jobSiteId === siteId && row.status !== "closed" && row.status !== "logged",
    ).length;

  const activeWorkspace = pendingWorkspace ?? data?.activeWorkspace;
  const personalWorkspace =
    activeWorkspace?.type === "personal" ||
    (!activeWorkspace && new URLSearchParams(search).get("workspace") === "personal");

  /**
   * A 403 or a 400 means the caller IS authenticated and merely named a
   * workspace they may not have — a forged `?workspace=`, or a stale
   * bookmark to a team they have left. The response carries no
   * `availableWorkspaces` in that case, and rendering an empty switcher
   * would strand them on a dead URL with no way back: the one thing the
   * switcher exists to prevent.
   *
   * Personal is safe to offer without asking the server, and only Personal
   * is: `listAvailableWorkspaces` puts it in every authenticated caller's
   * list unconditionally, because it names no tenant. No TEAM is ever
   * invented here — an unreachable team must stay unreachable.
   */
  /** The VIEWS section, as one reorderable list. The unfiltered item route
   * remains addressable, but its "All items" shortcut is intentionally hidden
   * while that destination is out of the navigation. */
  const viewRows: SidebarViewRow[] = [
    {
      id: "walks",
      name: "Walks",
      hint: "Every walk in this workspace, newest first.",
      d1: "M12 21s7-6.4 7-11a7 7 0 1 0-14 0c0 4.6 7 11 7 11Z",
      d2: "M12 12.4a2.4 2.4 0 1 0 0-4.8 2.4 2.4 0 0 0 0 4.8Z",
      active: page === "walks" && !openItemId,
      count: failure || data?.walkCount === undefined ? "—" : data.walkCount,
      href: hrefWith(hrefForPage("walks"), "view", null),
    },
    ...projectSavedViews(activeIntegration).filter((savedView) => savedView.id !== "all").map((savedView) => ({
      id: savedView.id as string,
      name: savedView.name,
      hint: savedView.hint,
      d1: savedView.d1,
      d2: savedView.d2,
      // A saved view is only ACTIVE when the item list (or its log variant)
      // is what is actually on screen. Highlighting "All items" while the
      // Brief is open would tell the user they are in two places at once.
      active: savedView.id === view && page === "items" && !openItemId,
      // Counted with the LIST's own predicate, never with `kind` alone.
      // A Procore-format project stores an observation as a daily-log row
      // (`kind: "log"`, `recordType: "observation"`), which is why
      // `matchesSavedView` exists — and why this line read `0` next to a
      // view that opened on 171 items. A count that disagrees with the list
      // it labels is worse than no count.
      count: failure
        ? "—"
        : savedView.id === "all"
          ? rows.length
          : rows.filter((row) => (!activeSiteId || row.jobSiteId === activeSiteId) && matchesSavedView(savedView, row)).length,
      // Clearing `page` and `item` matters: a saved view clicked from the
      // Brief must land ON the list, not quietly change a parameter of a page
      // that does not read it.
      // `page=items` is explicit now that a page-less URL lands on Home — a
      // saved view clicked from anywhere must land ON the list.
      href:
        (BASE_PATH !== MOUNT_PATH ? BASE_PATH : consoleUrl("/")) +
        (() => {
          const next = hrefWith(
            hrefWith(hrefWith(search, "page", "items"), "item", null),
            "view",
            savedView.id === "all" ? null : savedView.id,
          );
          return next.startsWith("?") ? next : "";
        })(),
    })),
    {
      id: "photos",
      name: "Photos",
      hint: "Every photo a walk captured, newest first.",
      // A camera: body, lens, and the bump over the viewfinder.
      d1: "M3 8.5h3.2l1.4-2.2h8.8l1.4 2.2H21v10H3v-10Z",
      d2: "M12 16.4a3.4 3.4 0 1 0 0-6.8 3.4 3.4 0 0 0 0 6.8Z",
      active: page === "photos" && !openItemId,
      count: failure || data?.photoCount === undefined ? "—" : data.photoCount,
      href: hrefWith(hrefForPage("photos"), "view", null),
    },
  ];
  /**
   * The three top destinations, as data rather than as three literal
   * elements — for the same reason the VIEWS rows are: a list that can be
   * REORDERED cannot be three hand-placed components.
   *
   * The PINNED admin block below is deliberately NOT one of these. Policies,
   * People and Invite are chrome, and their position must not depend on a
   * preference any more than it may depend on how many projects a workspace
   * has.
   */
  /** What the Review row stands for: waiting records and saved-for-later ones. */
  const reviewTotal = (data?.pendingReviewCount ?? 0) + (data?.savedForLaterCount ?? 0);
  const pageNavs = [
    {
      id: "brief",
      label: "Home",
      testId: "nav-brief",
      hint: "The press box view into your jobs.",
      active: page === "brief" && !openItemId,
      href: hrefForPage("brief"),
      d1: "M4 10.5 12 4l8 6.5V20h-5.5v-6h-5v6H4v-9.5Z",
      d2: undefined as string | undefined,
      count: undefined as number | undefined,
    },
    {
      id: "review",
      label: "Review",
      testId: "nav-review",
      hint: "Everything a walk generated: accept, edit or reject it.",
      active: page === "review" && !openItemId,
      href: hrefForReview(),
      // Every VIEWS row below carries a count; this one did not, so the
      // sidebar said nothing about the queue's size while the Review page
      // itself opened on "48 items to review". Counted by the server
      // over the whole scope (`listItems`' `pendingReviewCount`), so a filter
      // on the Item list cannot change it. Zero prints nothing rather than
      // "0" — an empty queue is not news. Records saved for later count:
      // they are still to review (the phone says so too), and a project
      // holding only those must not lose its way to them.
      count: reviewTotal ? reviewTotal : undefined,
      d1: "M20 6 9 17l-5-5",
      d2: undefined as string | undefined,
    },
    {
      // TEAM, and a top destination — where the design puts it. It was
      // "People", pinned down in the admin block with Settings and Invite,
      // which filed "who is walking, and what are they producing" under
      // administration. It is not chrome: on a team it is one of the four
      // questions the console exists to answer, and the design's own nav
      // reads Home, Review, Team, Ask.
      id: "team",
      label: "Team",
      testId: "nav-people",
      hint: "Everyone in this organization, their role, and what they have walked.",
      active: page === "people" && !openItemId,
      href: hrefForPage("people"),
      d1: "M9 4.8a3.2 3.2 0 100 6.4 3.2 3.2 0 000-6.4Z",
      d2: "M3.5 20c0-3.3 2.5-6 5.5-6s5.5 2.7 5.5 6M16 5.2A3.2 3.2 0 0118 11",
    },
    {
      id: "ask",
      label: "Ask Led AI",
      testId: "nav-ask-led",
      hint: "Ask a question about your jobs.",
      active: page === "ask" && !openItemId,
      href: hrefForPage("ask"),
      d1: "M13 2.5 4.5 13.5H12l-1 8 8.5-11H12l1-8Z",
      d2: undefined as string | undefined,
    },
  ]
    // A Personal workspace has no organization at all, so it has no team.
    // The row is kept off the rail rather than drawn as a destination that
    // can only ever say "No organization" — the same call that was made for
    // People before it. This was briefly removed to match the design's
    // artboard, which draws Team beside "Personal · Just you"; the artboard
    // is a mock with a fabricated roster, and the user restored the rule
    // (2026-09-07). Do not remove it again on the strength of that picture.
    .filter((nav) => nav.id !== "team" || !personalWorkspace)
    // Team Review is a destination even when inventory counts are loading,
    // unavailable, or zero. The count controls its badge, not access to the
    // queue. Personal workspaces keep their empty-queue hiding behavior.
    .filter((nav) => nav.id !== "review" || !personalWorkspace || reviewTotal > 0);

  /**
   * Each of the three lists remembers its own order, under its own key.
   *
   * `localStorage` is the right store: this is one viewer's display
   * preference, it is not shared with anybody, and it has to survive a
   * reload. It is also allowed to be missing — a private window throws from
   * the accessor itself — and `reorder.ts` answers "no stored order" for
   * every one of those cases, so the sidebar renders in its natural order and
   * simply forgets between reloads.
   */
  const navs = useReorder(ORDER_KEY.nav, pageNavs, (nav) => nav.id);
  const views = useReorder(ORDER_KEY.views, viewRows, (row) => row.id);

  /**
   * The project-scoped nav (`Led AI - Project Scoped Nav.dc.html`).
   *
   * With a project in scope, Records is ONE section in that project's own
   * vocabulary (`views`, as before) under its source's name. With All
   * projects, it is a section per source the workspace has — Procore,
   * Autodesk, Led AI only — and a record type opens `ProjectsForType`, the
   * list of that source's projects, rather than a table mixing every
   * project's records (where an Autodesk issue read as an observation).
   */
  const presentSources = SOURCE_ORDER.filter((source) => sites.some((site) => sourceOf(site) === source));
  const sourceParam = params.get("source");
  const scopedType: ScopedType | null =
    page === "walks" ? "walks"
      : page === "photos" ? "photos"
        : page === "items" && isSavedViewId(rawView) && rawView !== "all" ? rawView
          : null;
  // A list someone narrowed on purpose (Home's "stalled", a severity, a
  // column filter) stays the cross-project list it always was.
  const listNarrowed = ["stalledOnly", "severity", "groupBy", "sort"].some((key) => params.has(key)) ||
    [...params.keys()].some((key) => key.startsWith("f."));
  // One project is nothing to pick from and nothing to mix: its records
  // open directly, as they always have.
  const pickerSource: RecordSource | undefined =
    !activeSiteId && scopedType && !listNarrowed && !openItemId && sites.length > 1
      ? isRecordSource(sourceParam) && SOURCE_TYPES[sourceParam].includes(scopedType) && presentSources.includes(sourceParam)
        ? sourceParam
        : defaultSourceFor(scopedType, presentSources)
      : undefined;
  /**
   * A source with ONE project has nothing to pick: its page shows that
   * project's records in place (LED-928). A PRESENTATION choice, never a
   * scope change — `?site=` stays unset, the pill keeps saying All projects,
   * the sidebar keeps its source sections, and an All-projects pick in the
   * pill sticks. Only the page's data narrows (`scopeSiteId`).
   */
  const presentedSite = pickerSource
    ? (() => {
        const own = sites.filter((site) => sourceOf(site) === pickerSource);
        return own.length === 1 ? own[0]! : undefined;
      })()
    : undefined;
  const presentedSiteId = presentedSite?.id;
  // The page's vocabulary is the presented project's (Issues for Autodesk),
  // while the sidebar's stays All projects'.
  const pageView: SavedViewId = presentedSite
    ? projectSavedView(isSavedViewId(rawView) ? rawView : "all", presentedSite.syncSource)
    : view;
  const pageProps: ConsoleViewProps = presentedSiteId ? { ...viewProps, scopeSiteId: presentedSiteId } : viewProps;
  const hrefForScopedType = (source: RecordSource, type: ScopedType) => {
    const base = type === "walks" || type === "photos"
      ? hrefWith(hrefForPage(type), "view", null)
      : (BASE_PATH !== MOUNT_PATH ? BASE_PATH : consoleUrl("/")) +
        (() => {
          const next = hrefWith(hrefWith(hrefWith(search, "page", "items"), "item", null), "view", type);
          return next.startsWith("?") ? next : "";
        })();
    // A one-project workspace has no picker to skip (`pickerSource` above):
    // its links stay the list they always were. Scoping them to the project
    // changed the scope of every page after one click, so Home came back
    // as a page never loaded rather than the one already on screen.
    if (sites.length <= 1) return base;
    return hrefWith(base, "source", source);
  };
  const viewById = new Map(viewRows.map((row) => [row.id, row]));
  const orderOf = new Map(views.ordered.map((row, index) => [row.id, index]));
  const recordSections: Array<{
    key: string;
    source: RecordSource | null;
    label: string;
    meta?: string;
    rows: Array<{ id: string; name: string; hint: string; d1: string; d2?: string; active: boolean; count?: number | string; href: string }>;
    reorder: boolean;
  }> = activeSiteId || presentSources.length === 0
    ? [{
        key: "scoped",
        source: activeSiteId ? sourceOf(sites.find((site) => site.id === activeSiteId)) : null,
        label: activeSiteId ? scopedSectionLabel(sourceOf(sites.find((site) => site.id === activeSiteId))) : "Records",
        rows: views.ordered,
        reorder: true,
      }]
    : presentSources.map((source) => {
        const ids = new Set(sites.filter((site) => sourceOf(site) === source).map((site) => site.id));
        const activity = data?.siteActivity;
        // A server that predates `siteActivity` still answers the
        // workspace's totals, which are this section's when it is the only one.
        const fallback = (pick: "walks" | "photos") => {
          const whole = pick === "walks" ? data?.walkCount : data?.photoCount;
          return presentSources.length === 1 && whole !== undefined ? whole : "—";
        };
        const total = (pick: "walks" | "photos") =>
          activity ? [...ids].reduce((sum, id) => sum + (activity[id]?.[pick] ?? 0), 0) : fallback(pick);
        return {
          key: source,
          source,
          label: SOURCE_LABEL[source],
          meta: `${ids.size} proj`,
          // One order across sections: moving Photos up moves it in each.
          reorder: true,
          rows: [...SOURCE_TYPES[source]]
            .sort((x, y) => (orderOf.get(x === "issues" ? "obs" : x) ?? 99) - (orderOf.get(y === "issues" ? "obs" : y) ?? 99))
            .map((type) => {
              const known = viewById.get(type);
              return {
                id: type,
                name: typeLabel(source, type),
                hint: known?.hint ?? `${typeLabel(source, type)} on every ${SOURCE_LABEL[source]} project.`,
                d1: known?.d1 ?? "",
                d2: known?.d2,
                active: scopedType === type && (pickerSource ? pickerSource === source : presentSources.length === 1),
                count: failure ? "—" : type === "walks" || type === "photos" ? total(type) : rowsOfType(rows, type, ids).length,
                href: hrefForScopedType(source, type),
              };
            }),
        };
      });

  /**
   * WHO is signed in — Clerk first, the server's `caller` second.
   *
   * The Mongo `users` mirror the server reads carries only `userId` and
   * `clerkUserId` for these accounts; nothing ever wrote a name or an email
   * into it, because `upsertUserFromClerk` runs only from the Clerk webhook.
   * The browser is signed in through Clerk and has held the profile all
   * along. `identity.tsx` has the full account of it.
   *
   * `useAuthPerson` is a CONTEXT, not a Clerk hook: the shell must not import
   * Clerk or the ungated console (no publishable key) would crash on boot.
   */
  const person = useAuthPerson();
  const identity = resolveIdentity(person, data?.caller);

  const recoverable = failure?.status === 403 || failure?.status === 400;
  const availableWorkspaces: Workspace[] =
    data?.availableWorkspaces ??
    (knownWorkspaces.length
      ? knownWorkspaces
      : recoverable
        ? [{ type: "personal" }]
        : []);

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.canvas }}>
      {/* ===================== SIDEBAR ===================== */}
      {(mobile ? drawerPresent : true) && (
      <div
        data-testid="console-sidebar"
        data-nav={mobile ? "drawer" : navPref}
        data-closing={mobile && !drawerOpen ? "true" : undefined}
        data-scoped={activeSiteId ? "true" : "false"}
        style={{
          // LED-1151: the grounds are ALWAYS two-tone — the nav one step
          // darker than the main pane — and scoping into a project warms
          // BOTH by one step (`H.navAll`/`H.mainAll` → `H.navScoped`/
          // `H.mainScoped`). Flat, as the design draws it; the earlier lit
          // rail's gold blooms would muddy the one-step warm-up.
          background: activeSiteId ? H.navScoped : H.navAll,
          borderRight: `1px solid ${H.edge}`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          ...(mobile
            ? {
                // The design's off-canvas drawer: over the page, not in it.
                position: "fixed",
                left: 0,
                top: 0,
                bottom: 0,
                width: "min(300px, 86vw)",
                zIndex: 30,
                boxShadow: `0 10px 40px ${C.scrim}`,
              }
            : { width: rail ? RAIL_WIDTH : SIDEBAR_WIDTH, flexShrink: 0 }),
        }}
      >
        {/* --- the nav HEADER (LED-1151 "Home · projects"): the workspace
               mark and name, then the collapse control at the row's end.
               62px tall; on the 68px rail the row STACKS — mark over toggle —
               and centres. The workspace caret is a small tile INSIDE the
               workspace button and the collapse toggle sits 8px clear of it,
               so the two never crowd each other or the picker below. --- */}
        <div
          className="navhead"
          data-testid="nav-header"
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            ...(rail
              ? { flexDirection: "column", padding: "12px 0 8px" }
              : { height: 62, padding: "0 12px 0 14px" }),
          }}
        >
          <div
            style={{
              ...(rail ? {} : { flex: 1 }),
              minWidth: 0,
              position: "relative",
            }}
          >
          <button
            ref={pillRef}
            onClick={() => setSwitcherOpen((open) => !open)}
            aria-expanded={switcherOpen}
            aria-haspopup="menu"
            className={rail ? "kb navpill railrow" : "kb navpill"}
            data-testid="workspace-switcher"
            data-tip={rail ? "Switch workspace" : undefined}
            onMouseEnter={rail ? pinTip : undefined}
            onFocus={rail ? pinTip : undefined}
            style={{
              width: rail ? 34 : "100%",
              display: "flex",
              alignItems: "center",
              gap: rail ? 0 : 8,
              height: 44,
              padding: 0,
              borderRadius: 10,
              textAlign: "left",
              background: "transparent",
            }}
          >
            {/* The product's own badge mark — hard hat, LED AI across the
                brim, glasses. Bare, with no tile behind it: the mark HAS a
                shape, and boxing it made the rail's one piece of brand look
                like a favicon someone pasted in. */}
            <span data-testid="workspace-mark" style={{ display: "flex", flexShrink: 0, opacity: 0.9 }}>
              <Lockup variant="mark" height={24} />
            </span>
            {!rail && (
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: "block",
                    fontSize: 15,
                    fontWeight: 800,
                    letterSpacing: "-0.3px",
                    lineHeight: 1.15,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: H.ink,
                  }}
                >
                  {activeWorkspace
                    ? workspaceLabel(activeWorkspace)
                    : failure
                      ? "Unavailable"
                      : "…"}
                </span>
                <span
                  style={{
                    display: "block",
                    fontSize: 12,
                    color: H.muted,
                    marginTop: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {activeWorkspace
                    ? activeWorkspace.type === "team"
                      ? "Team"
                      : "Just you"
                    : failure
                      ? "Pick another below"
                      : "Loading"}
                </span>
              </span>
            )}
            {!rail && (
              // The workspace caret: a small tile so it reads as a control,
              // turning with the popover. 20px, so it and the 30px collapse
              // toggle beside it keep 8px of daylight between them.
              <span
                className="wscaret"
                data-testid="workspace-caret"
                style={{
                  width: 20,
                  height: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  borderRadius: 6,
                  border: `1px solid ${H.edge}`,
                  background: H.white,
                  transform: switcherOpen ? "rotate(180deg)" : undefined,
                }}
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{ display: "block" }} aria-hidden>
                  <path d="M8 10.5l4 4 4-4" stroke={H.sub} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            )}
          </button>

          {switcherOpen && (
            <WorkspacePopover
              anchor={pillRef.current}
              workspaces={availableWorkspaces}
              activeWorkspace={activeWorkspace}
              search={search}
              onNavigate={navigate}
              onDismiss={closeSwitcher}
            />
          )}
          </div>
          <NavToggle
            // Keyed on the state so the button REMOUNTS across the collapse:
            // the header flips from a row to a column and no transition can
            // interpolate that, so the toggle re-runs `ledai-nav-toggle`.
            key={rail ? "rail" : "open"}
            tip={mobile ? "Hide sidebar" : rail ? "Expand" : "Collapse"}
            testId="nav-hide"
            rail={rail}
            onClick={() =>
              mobile ? setDrawerOpen(false) : setNav(rail ? "open" : "rail")
            }
          />
        </div>

        {/* --- the SCOPE, under the workspace it belongs to ---
            "All projects" or one project, opening the searchable picker
            (pinned group, most-recent list, 100+ projects). `?site=` is the
            filter every page already reads, so choosing here narrows the
            whole console and follows the reader across it. On the rail the
            box folds to the scope dot. */}
        <div
          style={{
            flexShrink: 0,
            margin: rail ? "0 15px 6px" : "0 10px 6px",
            // On the rail the scope dot and the ⌘K circle stack, centred.
            ...(rail ? { display: "flex", flexDirection: "column", alignItems: "center", gap: 6 } : {}),
          }}
        >
          {/* LED-1151: the nav header's scope box in BOTH states — it folds
              to the scope dot on the 68px rail. The rail also keeps the ⌘K
              circle under it: the dot names nothing, and search is how a
              reader reaches a project the rail can no longer name. */}
          <ProjectPicker
            sites={sites}
            activeSiteId={activeSiteId}
            onSelect={(siteId) => navigate(hrefWith(search, "site", siteId))}
            search={search}
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            rail={rail}
          />
          {rail && (
          <button
            data-testid="open-cmdk"
            className={rail ? "kb railrow" : "kb"}
            title="Search projects, walks and items (⌘K)"
            data-tip={rail ? "Search (⌘K)" : undefined}
            onMouseEnter={rail ? pinTip : undefined}
            onFocus={rail ? pinTip : undefined}
            onClick={() => setPaletteOpen(true)}
            style={{
              width: 30,
              height: 30,
              flex: "none",
              borderRadius: 999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px solid ${H.edge}`,
              background: "transparent",
              cursor: "pointer",
            }}
          >
            <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
              <circle cx="8" cy="8" r="5.4" stroke={H.sub} strokeWidth="1.7" />
              <path d="M12 12l4 4" stroke={H.sub} strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </button>
          )}
        </div>

        {/* --- nav ---
            Scrolls. Everything in here can be pushed below the fold by a long
            project list, which is precisely why the ADMIN block below is not
            in here. */}
        <div
          className="sb"
          data-testid="sidebar-scroll"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            // The gutter is 16px in BOTH states (see `.navhead`), so the rows
            // inside never reflow — they narrow with the sidebar and the
            // labels are clipped rather than re-laid-out. `overflow-x` has to
            // be hidden explicitly for that: a 244px row inside a 40px column
            // would otherwise grow a horizontal scrollbar on the way down.
            overflowX: "hidden",
            // LED-1151: the list sits 10px in from the nav's edges in BOTH
            // states; the rail centres its glyphs by the rows' own padding.
            padding: "4px 10px",
          }}
        >
          {navs.ordered.map((nav) => (
            <PageNav
              key={nav.id}
              label={nav.label}
              hint={nav.hint}
              active={nav.active}
              href={nav.href}
              onNavigate={navigate}
              icon={NAV_ICONS[nav.id]}
              d1={nav.d1}
              d2={nav.d2}
              count={nav.count}
              // The one gold thing in the rail. Review's number is a QUEUE —
              // work waiting on the reader — where every other count in the
              // column is inventory, and the design draws that difference
              // rather than leaving the reader to infer it from the row.
              badge={nav.id === "review"}
              testId={nav.testId}
              reorder={navs.rowProps(nav.id)}
              rail={rail}
            />
          ))}

          {/* "Records" in the redesign: these rows are the record types
              a walk produces, and "Views" named the mechanism instead of the
              thing. */}
          {recordSections.map((section) => (
          <Fragment key={section.key}>
          <SectionLabel
            rail={rail}
            testId={`nav-section-${section.key}`}
            mark={section.source ? <SourceMark source={section.source === "led" ? undefined : section.source} size={12} /> : undefined}
            meta={section.meta}
          >
            {section.label}
          </SectionLabel>
          {section.rows.map((row) => (
            <PageNav
              key={`${section.key}-${row.id}`}
              label={row.name}
              hint={row.hint}
              active={row.active}
              href={row.href}
              onNavigate={navigate}
              icon={NAV_ICONS[row.id]}
              d1={row.d1}
              d2={row.d2}
              count={row.count}
              testId={`nav-view-${row.id}`}
              reorder={section.reorder && orderOf.has(row.id) ? views.rowProps(row.id) : undefined}
              rail={rail}
              // The Records rows ARE the destination rows, one notch shorter.
              // They used to be a second copy of the same markup that had
              // drifted from it — a 10px radius against 11, a 14px glyph
              // against 15, and `faint` for the selected row where the
              // destinations above used the design's white surface, so the
              // rail selected a Record and a page in two different ways.
              dense
            />
          ))}
          </Fragment>
          ))}

          {/* "Project details" — the design's third section, and only with a
              project in scope. A directory and a location tree belong to ONE
              project, so under All projects there is nothing to open. Counts
              come from the same read the pages draw; "—" until it lands (or
              when there is nothing uploaded yet). */}
          {activeSiteId && (
            <>
              <SectionLabel rail={rail}>Project details</SectionLabel>
              {(["directory", "locations"] as const).map((id) => {
                const loaded = details.data?.projectId === activeSiteId ? details.data : null;
                // The summary knows a half once anyone has read it (or a file
                // was uploaded); until then "—", and no provider call is made
                // to find out.
                const count =
                  id === "directory"
                    ? loaded?.directory?.people
                    : loaded?.locations?.total;
                return (
                  <PageNav
                    key={id}
                    label={id === "directory" ? "Directory" : "Locations"}
                    hint={
                      id === "directory"
                        ? "Everyone on this project, by company."
                        : "Every location in this project, as a hierarchy."
                    }
                    active={page === id && !openItemId}
                    href={hrefForPage(id)}
                    onNavigate={navigate}
                    icon={NAV_ICONS[id]}
                    d1={
                      id === "directory"
                        ? "M9 4.8a3.2 3.2 0 100 6.4 3.2 3.2 0 000-6.4Z"
                        : "M12 21s7-6.4 7-11a7 7 0 1 0-14 0c0 4.6 7 11 7 11Z"
                    }
                    d2={
                      id === "directory"
                        ? "M3.5 20c0-3.3 2.5-6 5.5-6s5.5 2.7 5.5 6"
                        : "M12 12.4a2.4 2.4 0 1 0 0-4.8 2.4 2.4 0 0 0 0 4.8Z"
                    }
                    count={count ? count : "—"}
                    testId={`nav-${id}`}
                    rail={rail}
                    dense
                  />
                );
              })}
            </>
          )}

          {/* Settings / Policies and Invite people, in the SAME scrolling list
              as everything above (user, 2026-10-26: more tabs are coming, and
              a second, pinned list would fight the first for the height).
              They used to be pinned below it, from when a 27-row project
              list could push them off screen; the sidebar no longer lists
              projects. */}
          <div data-testid="sidebar-admin" style={{ marginTop: 18 }}>
          <PageNav
            // "Settings" in both workspaces (LED-1151). The TEAM row keeps
            // its `nav-policies` id: it is still the company policies page.
            label="Settings"
            testId={personalWorkspace ? "nav-settings" : "nav-policies"}
            hint={personalWorkspace
              ? "The same personal preferences used by your mobile app."
              : "Company settings pushed to every phone and pair of glasses. A locked group is read-only on site."}
            active={page === "policies" && !openItemId}
            href={hrefForPage("policies")}
            onNavigate={navigate}
            icon={SettingsIcon}
            d1="M12 3l8 3v6c0 4.4-3.2 7.6-8 9-4.8-1.4-8-4.6-8-9V6l8-3Z"
            d2="m9 12 2 2 4-4"
            rail={rail}
            dense
          />
          {/* People is TEAM now, and it is a top destination — see the
              `pageNavs` entry. The design's bottom block is Settings and
              Invite people, and nothing else. */}
          <PageNav
            label="Invite people"
            hint="Invite internal staff to this organization."
            active={page === "invite" && !openItemId}
            href={hrefForPage("invite")}
            onNavigate={navigate}
            icon={UserPlus}
            d1="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
            d2="M9 3.2a3.8 3.8 0 100 7.6 3.8 3.8 0 000-7.6ZM19 8v6M22 11h-6"
            rail={rail}
            dense
          />
          </div>

          {/* No PROJECTS section.

              The rail lists what a record IS — walks, daily logs, tasks,
              punch, photos — and the project is a FILTER across all of
              them, which the topbar's project pill already applies and
              carries between screens. Listing projects here made the
              sidebar answer two different questions in one column, and on
              a real workspace the second answer was 27 rows long and
              pushed the first below the fold (user-directed,
              2026-09-07). */}
        </div>

        {/* --- footer --- */}
        <div
          style={{
            flexShrink: 0,
            // 18px at the foot is the aside's own bottom padding in the
            // design; the rule that used to run across here went with the
            // one above the admin rows, for the same reason.
            //
            // The 14px at the TOP is not decoration: with no rule left to
            // separate them, the identity sat directly under "Invite people"
            // and read as another nav row rather than as the block the rail
            // ends on (user-directed, 2026-09-08).
            // The account block at the foot, under a hairline (the design's
            // 64px account row), with the live status beneath it.
            padding: "12px 16px 14px",
            borderTop: `1px solid ${H.edge}`,
          }}
        >
          {/* No search ROW here. Search is the 30px circle beside the scope
              pill at the head of the rail, where the design puts it — a
              full-width row for it at the foot was a second search
              affordance in one column. ⌘K still opens the palette from
              anywhere; the circle carries the shortcut in its title. */}
          <div
            data-testid="sidebar-identity"
            style={{
              display: "flex",
              alignItems: "center",
              gap: rail ? 0 : 10,
              // The name below carries `flex: 1`, so the avatar is carried
              // leftward by the name's own collapse and lands centred on the
              // rail without a `justify-content` flip to snap it there.
              padding: rail ? "0 5px" : "0 4px",
              // Clear of the live pill beneath. The name and its SIGNED IN
              // caption are one two-line block; without this they crowded the
              // pill and the three lines read as one run-on stack.
              marginBottom: 12,
            }}
          >
            {/*
              NO avatar here.

              There used to be one: a 32px circle of initials computed from
              whatever `callerLabel` returned. For the real accounts that
              meant initials of a CLERK ID — "U3" — drawn immediately beside
              Clerk's own `<UserButton>` avatar, so the footer showed two
              profile bubbles for one person and only one of them was real.
              Clerk's is the real one, and it is the one that stays.
            */}
            {/* Who is signed in, in words. Dropped on the rail: 64px cannot
                hold a name, and the identity CONTROL beside it (Clerk's own
                avatar, when there is one) is the thing a reader clicks. */}
            <NavLabel rail={rail} style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  // An id is not a name, so it is not set in the text face
                  // either — the mono column is where this console puts
                  // machine strings.
                  fontFamily: identity.source === "id" ? FONT_MONO : undefined,
                  fontSize: identity.source === "id" ? 12.65 : 14.95,
                  fontWeight: 700,
                  letterSpacing: "-0.3px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={identity.label || undefined}
                data-testid="signed-in-as"
              >
                {identity.source === "none"
                  ? failure?.status === 401
                    ? "Not signed in"
                    : "—"
                  : identity.label}
              </div>
              <div
                data-testid="identity-caption"
                style={{ ...META, fontSize: 9.77, color: C.sub, letterSpacing: "0.7px", marginTop: 1 }}
              >
                {identityCaption(identity, !!data, failure?.status === 401)}
              </div>
            </NavLabel>
            {/*
              The way OUT, and the way to the other account.

              It sits beside the identity it acts on, in the pinned footer,
              so it cannot be scrolled away by a long project list or
              unmounted by a navigation. `AuthGate` decides what goes here:
              Clerk's `<UserButton>` when Clerk is configured, nothing at all
              in the ungated console, which has no session to end.

              This was the whole of defect 1 — the control existed and was
              mounted nowhere, so the console could be entered and never
              left, and the second organization (reachable only by signing in
              as the other account) could not be reached by a human at all.
            */}
            <AuthIdentity />
          </div>
          {/* No Live pill: the user removed it from the console (2026-10-26).
              The connection state is still exposed, invisibly, for the live
              e2e checks that read whether the stream is up. */}
          <span hidden data-testid="live-status" data-live-status={live} />
        </div>
      </div>

      )}

      {mobile && drawerPresent && (
        <div
          data-testid="console-scrim"
          className="navscrim"
          // Held past `drawerOpen` for the same reason the drawer is: the
          // scrim has to fade back out WITH the door rather than blinking
          // off a frame before it.
          data-closing={drawerOpen ? undefined : "true"}
          onClick={() => setDrawerOpen(false)}
          style={{ position: "fixed", inset: 0, background: C.scrim, zIndex: 25 }}
        />
      )}

      {/* ===================== MAIN ===================== */}
      <div
        data-testid="console-main"
        style={{
          ...({ "--home-pane-top": mobile && !drawerOpen ? "63px" : "0px" } as React.CSSProperties),
          // FLUSH — the settled treatment from the "Right pane edge options"
          // design (option B): the pane runs edge-to-edge and only the
          // sidebar border divides them. Nothing can look cut off because
          // nothing is rounded. No bottom inset either: the Ask composer
          // spans the pane's bottom over a fade and content scrolls under it
          // BY DESIGN — every scrolling body ends in
          // `ASK_COMPOSER_CLEARANCE` of padding instead, so any control can
          // always be scrolled fully above it.
          flex: 1,
          minWidth: 0,
          // The redesign's page ground — a half-step warmer than `surface`,
          // so the white cards on it read as raised rather than as the page
          // itself. This is what makes the whole console look layered.
          background: activeSiteId ? H.mainScoped : H.mainAll,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {/* NO topbar on a desktop. The design has none: the scope pill moved
            into the rail, under the workspace it narrows, and with it gone
            there was nothing left up here but a 50px rule across every page.

            It survives on MOBILE for the one thing the rail cannot do when
            the rail is a closed drawer — get itself opened again. */}
        {mobile && !drawerOpen && (
          <div
            data-testid="console-topbar"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "9px 14px",
              borderBottom: `1px solid ${C.line}`,
              flexShrink: 0,
            }}
          >
            <NavToggle
              tip="Show sidebar"
              testId="nav-show"
              big
              onClick={() => setDrawerOpen(true)}
            />
          </div>
        )}
        {/*
          The view swap, as a MOVE rather than a blink.

          Opening a walk or a punch item replaces the whole pane in one frame
          — these are siblings in a ternary, not an overlay — so a click on a
          row produced an instantaneous cut with nothing connecting the list
          to the record that came out of it. The wrapper is keyed on WHAT IS
          OPEN, so the entrance replays on opening a record and stays put
          through a live refresh of the record already on screen.

          `viewKey` deliberately does not include the whole search string: a
          filter or sort change is not an opening, and re-running the
          animation for one would make every chip press flash the page.
        */}
        <ReviewSyncToast />
        <div
          data-testid="view-enter"
          // A REAL box, and an unkeyed one.
          //
          // The first cut keyed this wrapper on `viewKey`, which does replay
          // the animation — by remounting the entire view beneath it. That
          // threw away each view's `useResource` state and refetched on every
          // open, which the shell's own "re-queries the server" test caught
          // as one request too many. Nothing here may remount.
          //
          // So the entrance is re-triggered by changing the animation's NAME
          // instead (`enterParity` alternates between two identical
          // keyframes) — the same rule the drawer taught: an animation
          // restarts when its name changes, and only then.
          //
          // It also cannot be `display: contents`: that generates no box, and
          // a thing with no box has nothing to fade or translate. The box
          // therefore reproduces exactly what the pane gave its children —
          // a full-height flex column — so inserting it changes no layout.
          className={enterParity === 0 ? "viewenter-a" : "viewenter-b"}
          style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
        >
        {openItemId ? (
          <ItemDetail
            {...viewProps}
            itemId={openItemId}
            backHref={backHref}
            onBack={() => navigate(backHref)}
          />
        ) : pickerSource && !presentedSiteId && scopedType && data ? (
          <ProjectsForType
            source={pickerSource}
            type={scopedType}
            sites={sites}
            rows={rows}
            activity={data.siteActivity}
            hrefForProject={(siteId) => hrefWith(hrefWith(search, "site", siteId), "source", null)}
            onOpenProject={(siteId) => navigate(hrefWith(hrefWith(search, "site", siteId), "source", null))}
          />
        ) : page === "brief" ? (
          <Brief {...viewProps} />
        ) : page === "review" ? (
          <ReviewQueue {...viewProps} />
        ) : (page === "walks" || page === "photos") && scopedType && !activeSiteId && !listNarrowed && !data ? (
          // Under All projects the page cannot know yet whether its source has
          // one project to show in place (LED-928). Its own loader, rather
          // than a read of every project's walks that is thrown away a moment
          // later.
          page === "walks" ? <WalksSkeleton /> : <LoadingPanel shape="photos" />
        ) : page === "walks" ? (
          <AllWalks {...pageProps} />
        ) : page === "photos" ? (
          <Photos {...pageProps} />
        ) : page === "directory" || page === "locations" ? (
          <ProjectDetails
            {...viewProps}
            tab={page}
            // Before the workspace loads, a scoped URL is still scoped: the
            // page shows its loading state, not the "pick a project" list.
            site={
              activeSiteId
                ? sites.find((site) => site.id === activeSiteId) ?? (data ? null : { id: activeSiteId, name: "" })
                : null
            }
            sites={sites}
            details={details}
            onReload={() => setDetailsReload((n) => n + 1)}
          />
        ) : page === "project" && openProjectId ? (
          <ProjectHome {...viewProps} jobSiteId={openProjectId} />
        ) : page === "project-brief" && openProjectId ? (
          <ProjectBrief {...viewProps} jobSiteId={openProjectId} />
        ) : page === "ask" ? (
          <AskLed {...viewProps} />
        ) : page === "policies" ? (
          <Policies {...viewProps} />
        ) : page === "people" ? (
          <People {...viewProps} />
        ) : page === "invite" ? (
          <Invite {...viewProps} />
        ) : page === "walk" && openWalkId ? (
          <WalkDetail
            {...viewProps}
            visitId={openWalkId}
            backHref={walkBackHref}
            onBack={() => navigate(walkBackHref)}
          />
        ) : pageView === "log" ? (
          // The saved log view IS the structured Daily Log — see the file
          // header. The design does the same thing.
          <DailyLog {...pageProps} integration={presentedSite ? presentedSite.syncSource : activeIntegration} />
        ) : (
          <ItemList
            data={data}
            failure={failure}
            refreshFailure={refreshFailure}
            loading={loading}
            view={pageView}
            // A presented project (LED-928) narrows the rows drawn, not the
            // scope: the list is that one project's, the URL is not.
            activeSiteId={activeSiteId ?? presentedSiteId ?? null}
            sort={data?.sort ?? "attention"}
            onSort={(next: ConsoleSort) => navigate(hrefWith(search, "sort", next))}
            // A column sort writes two parameters, so it cannot go through
            // `hrefWith` once — the column and its direction have to land in
            // the same navigation or the first would render with the second
            // still stale.
            onColumnSort={(next) => {
              let href = search;
              for (const [key, value] of columnSortParams(next)) {
                href = hrefWith(href, key, value);
              }
              navigate(href);
            }}
            onColumnFilter={(column, value) =>
              navigate(hrefWith(search, columnFilterParam(column), value))
            }
            stalledOnly={data?.stalledOnly ?? false}
            severities={data?.severities ?? null}
            onSeverity={(next) => navigate(hrefWith(search, "severity", next))}
            // ONE navigation, not three. `hrefWith` builds from the search it
            // is handed, so calling the three setters in a row would start each
            // from the same stale query and land only the last.
            onClearFilters={() => {
              const params = new URLSearchParams(queryOf(search));
              // `view` is deliberately NOT touched. With the Kind filter gone
              // the view is the page you are standing on, and resetting it
              // would take a reader off Observations and onto All items —
              // navigation dressed up as clearing a filter.
              params.delete("stalledOnly");
              params.delete("severity");
              // ...and every column filter. `Clear all` is the row's only way
              // back now that Assignee is a card in it, so a clear that left
              // `f.company` set would hand back a list still narrowed by a
              // control the reader had already cleared from.
              for (const [key] of clearColumnFilterParams(ITEM_COLUMN_FILTERS)) {
                params.delete(key);
              }
              // ...and the records table's own (status, priority, trade, type).
              for (const key of RECORD_FILTER_KEYS) params.delete(columnFilterParam(key));
              const query = params.toString();
              navigate(query ? `?${query}` : location.pathname);
            }}
            personalHref={viewProps.personalHref}
            onGoPersonal={viewProps.onGoPersonal}
            hrefForItem={hrefForItem}
            onOpenItem={viewProps.onOpenItem}
            search={search}
          />
        )}
        </div>
      </div>

      {/* ⌘K sits OUTSIDE the main pane so it dims the sidebar too — it
          searches the whole workspace, not the page you happen to be on. */}
      <CommandK
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        search={search}
        onOpenItem={viewProps.onOpenItem}
        onOpenProject={viewProps.onOpenProject}
        onOpenWalk={viewProps.onOpenWalk}
      />

      {/*
        Ask Led, from wherever the reader already is — as the settled
        bottom-of-pane COMPOSER ("Right pane edge options", askLed:
        "Composer"), not the old floating bubble.

        It lives HERE and not in one view because a question occurs to a
        reader on the page that prompted it — the Review queue, a walk, a
        project — and the Brief was the only screen that had it. The phone's
        Ask is a tab reachable from every screen; this is the console's
        equivalent.

        Exclusions are structural rather than remembered:

         - **The Ask tab itself.** The composer is a shortcut INTO that tab,
           and it would sit directly on top of the tab's own composer — two
           Ask inputs on one screen, one of them covering the other.
         - **The sign-in screen.** Nothing to exclude: `AuthGate` renders this
           shell only inside `<SignedIn>`, so a bubble mounted here cannot
           appear beside a sign-in form.
         - **HOME.** The console redesign puts the composer INLINE, upper
           middle, as the thing Home is built around; a second one pinned to
           the bottom of the same page is the same two-inputs-on-one-screen
           problem as the Ask tab.
         - **REVIEW.** The queue is a focused, independently scrolling
           decision workspace. A fixed chat surface covers its record actions
           and takes the keyboard focus away from inline fields.

         - **PHOTOS.** Photo search already lives at the top of this page;
           keep the gallery unobstructed by a second search surface.

        Every page that keeps the composer ends in `ASK_COMPOSER_CLEARANCE`
        of padding, so no control is ever stuck underneath it.
      */}
      {page !== "ask" && page !== "brief" && page !== "review" && page !== "photos" && (
        <AskComposer
          search={search}
          // What the reader is looking at, decided HERE — the shell already
          // resolved every one of these to choose the view above, and letting
          // the composer re-derive them from `search` would give the
          // suggestion strip a second, drifting idea of which page is open.
          page={{
            page,
            view,
            itemOpen: !!openItemId,
            walkOpen: !!openWalkId,
            projectOpen: page === "project" && !!openProjectId,
            projectName: openProjectId
              ? (sites.find((site) => site.id === openProjectId)?.name ?? null)
              : null,
          }}
          onContinue={(question) => navigate(hrefWith(hrefForPage("ask"), "ask", question))}
          onOpenAsk={() => navigate(hrefForPage("ask"))}
          sidebarVisible={sidebarVisible}
        />
      )}
      {/* Rejection-reason banner (SPEC §5): hosted at the shell so it
          outlives the queue row that triggered it — the card swipes away on
          a decided reject, and the prompt must keep its 7s life anyway. */}
      <RejectReasonBannerHost />
      <IntegrationsDrawer
        open={integrationsOpen}
        onClose={() => setIntegrationsOpen(false)}
        accountName={identity.label || "You"}
        // Procore's developer sandbox is for Led AI staff only — and for the
        // ungated local console, which has no Clerk person at all.
        showSandbox={person ? looksLikeLedStaff(person.email) : true}
        // A sign-in or sign-out changes what every provider-backed read
        // can see: drop the session's copies and re-ask the summary.
        onChanged={() => {
          clearDetailsCache();
          setDetailsReload((n) => n + 1);
        }}
      />
    </div>
  );
}
